import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Form, Card } from "react-bootstrap";
import { useSelector } from "react-redux";

import { PlusIcon } from "../../../shared/icons/Icons";
import { getCurrentPlan } from "../../../shared/services/adminApi";

import '../home/Dashboard.scss';
import './PlanList.scss';

export default function PlanList(props) {

    const customDate = (dataValue) => {
        if (!dataValue) return '- - -';
        const date = moment.utc(dataValue).local();
        return `${date.format('MMM D, YYYY, HH:mm A')}`;
    };

    const dataTable = useRef(null);
    const [search, setSearch] = useState("");
    
    // User role detection
    const loggedInUserData = useSelector((state) => state.user);
    const userRole = loggedInUserData?.role || loggedInUserData?.user_type || loggedInUserData?.type || loggedInUserData?.userRole;
    const isAdmin = userRole === "admin";
    
    // Current plan state for non-admin users
    const [currentPlan, setCurrentPlan] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [transactionFilter, setTransactionFilter] = useState("All");

    // Mock transaction data (placeholder until backend API is ready)
    const [mockTransactions] = useState([
        {
            id: 1,
            type: 'credit',
            label: 'Credit Top-up',
            date: '2026-07-15T11:30:00Z',
            amount: 500,
            currency: 'INR',
            credits: 50,
            hasInvoice: true,
        },
        {
            id: 2,
            type: 'subscription',
            label: 'Standard • Monthly',
            date: '2026-07-27T09:02:00Z',
            amount: 1700,
            currency: 'INR',
            credits: 100,
            hasInvoice: true,
        },
        {
            id: 3,
            type: 'subscription',
            label: 'Standard • Monthly',
            date: '2026-06-27T09:02:00Z',
            amount: 99,
            currency: 'INR',
            credits: 100,
            hasInvoice: true,
        },
        {
            id: 4,
            type: 'credit',
            label: 'Credit Top-up',
            date: '2026-06-10T14:45:00Z',
            amount: 300,
            currency: 'INR',
            credits: 30,
            hasInvoice: false,
        },
        {
            id: 5,
            type: 'subscription',
            label: 'Standard • Monthly',
            date: '2026-05-27T09:02:00Z',
            amount: 249,
            currency: 'INR',
            credits: 100,
            hasInvoice: true,
        },
    ]);

    // Fetch current plan for non-admin users
    useEffect(() => {
        if (!isAdmin) {
            loadCurrentPlan();
        }
    }, [isAdmin]);

    const loadCurrentPlan = async () => {
        setLoadingPlan(true);
        try {
            const response = await getCurrentPlan();
            if (response?.data?.data) {
                setCurrentPlan(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching current plan:", error);
        } finally {
            setLoadingPlan(false);
        }
    };

    useEffect(() => {
        if (search) {
            let filters = [];

            if (search?.trim()) {
                filters.push(
                    { field: 'name', value: search, op: 'LIKE', join: 'OR' },
                    { field: 'code', value: search, op: 'LIKE', join: 'OR' },
                    { field: 'summary', value: search, op: 'LIKE', join: 'OR' },
                    { field: 'description', value: search, op: 'LIKE', join: 'OR' }
                );
            }

            if (filters.length) {
                dataTable.current.searchData(filters);
            } else {
                clearData();
            }
        }
    }, [search]);

    const navigateToAddPlan = () => {
        props.history.push('/plans/add');
    }

    const clearData = () => {
        setSearch("");
        dataTable.current?.searchData([]);
    }

    const formatCurrency = (amount, currency) => {
        if (!amount || amount === 0) return 'Free';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency || 'INR',
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <div className="assignment-list main-content animate-fade-in plans-list-container">
            {isAdmin ? (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex flex-column">
                        <h3 className="content-label">Plans</h3>
                        <p className='content-sub-label fw-bold text-gray'>Manage subscription plans and pricing</p>
                    </div>
                    <div className="d-flex gap-3">
                        <div className="btn btn-solid-blue fw-bold" onClick={() => { navigateToAddPlan() }}><PlusIcon /> Add Plan</div>
                    </div>
                </div>
            ) : (
                <div className="pi-header">
                    <div className="pi-header-text">
                        <h3 className="content-label">Plans & Invoices</h3>
                        <p className="content-sub-label">Manage your plan and view transaction history</p>
                    </div>
                    
                </div>
            )}

            {/* Plans & Invoices Section - Only for non-admin users */}
            {!isAdmin && (
                <div className="plans-invoices-container">
                    
                    {/* Current Plan Card */}
                    {loadingPlan ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
                            <img className='loader-icon' src={`${process.env.PUBLIC_URL}/static/images/loader.svg`} alt="loader-icon" />
                        </div>
                    ) : currentPlan && currentPlan.length > 0 ? (
                        currentPlan.map((planItem, index) => {
                            const amount = planItem.plan?.price?.amount;
                            const billingCycle = planItem.plan?.price?.billing_cycle;
                            const availableCredits = planItem.subscription?.available_count ?? 0;
                            const usedCredits = planItem.subscription?.used_count ?? 0;
                            const isFreePlan = amount === 0 || billingCycle === 'FREE';

                            return (
                                <Card className="current-plan-card" key={planItem.subscription?.id || planItem.id || `${planItem.plan?.id}-${index}`}>
                                    <Card.Body>
                                        {/* Plan Name + Action Button */}
                                        <div className="plan-name-section">
                                            <h4 className="plan-name">{planItem.plan?.name || 'N/A'}</h4>
                                            {isFreePlan ? (
                                                <button
                                                    className="upgrade-btn"
                                                    onClick={() => props.history?.push('/plans')}
                                                >
                                                    Upgrade to Pro <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.00004 1.33331L9.60004 5.99998L14.6667 5.99998L10.5334 8.86665L12.1334 13.5333L8.00004 10.6666L3.86671 13.5333L5.46671 8.86665L1.33337 5.99998L6.40004 5.99998L8.00004 1.33331Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                                </button>
                                            ) : (
                                                <span className="current-plan-badge">Current Plan</span>
                                            )}
                                        </div>

                                        {/* Credits Row */}
                                        <div className="credits-row">
                                            <p className="credits-count">
                                                <span className="credits-icon">
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"></path></svg>
                                                </span>
                                                {availableCredits} <span className="credits-label">credits available</span>
                                                {usedCredits > 0 && (
                                                    <span className="credits-used"> ({usedCredits} used)</span>
                                                )}
                                                <svg className="credits-chevron" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </p>
                                            <button type="button" className="manage-plan-link" onClick={() => props.history?.push('/plans')}>
                                                Manage Plan
                                                <svg viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })
                    ) : (
                        <Card className="current-plan-card">
                            <Card.Body>
                                <div className="alert alert-info mb-0">No active plan found. Please contact administrator.</div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Transactions Table */}
                    <hr className="pi-divider" />
                    <div className="transactions-section">
                        <div className="transactions-header">
                            <h5>Transactions</h5>
                            <div className="transactions-meta">
                                <div className="pagination-arrows">
                                    <button className="pagination-arrow" onClick={() => {}} disabled>{'<'}</button>
                                    <span className="pagination-info">
                                        1–{Math.min(mockTransactions.length, 10)} of {mockTransactions.length}
                                    </span>
                                    <button className="pagination-arrow" onClick={() => {}}>{'>'}</button>
                                </div>
                                <div className="filter-dropdown">
                                    <Form.Select
                                        size="sm"
                                        value={transactionFilter}
                                        onChange={(e) => setTransactionFilter(e.target.value)}
                                    >
                                        <option value="All">All</option>
                                        <option value="Subscription">Subscription</option>
                                        <option value="Credit">Credit</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>

                        {mockTransactions && mockTransactions.length > 0 ? (
                            <div className="transactions-table-wrapper">
                                <table className="transactions-table">
                                    <tbody>
                                        {mockTransactions.map((tx) => (
                                            <tr key={tx.id}>
                                                <td className="transaction-icon-cell">
                                                    <span className={`tx-icon ${tx.type === 'credit' ? 'credit-icon' : 'subscription-icon'}`}>
                                                        {tx.type === 'credit' ? (
                                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a.75.75 0 01.75.75v6.5h6.5a.75.75 0 010 1.5h-6.5v6.5a.75.75 0 01-1.5 0v-6.5H2.75a.75.75 0 010-1.5h6.5V2.75A.75.75 0 0110 2z"></path></svg>
                                                        ) : (
                                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"></path></svg>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="tx-info">
                                                    <span className="tx-info-name">{tx.label}</span>
                                                    <span className="tx-info-date">{customDate(tx.date)}</span>
                                                </td>
                                                <td className="tx-amount">{formatCurrency(tx.amount, tx.currency)}</td>
                                                <td className="tx-credits">
                                                    <span className="credits-diamond">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#A5B4FC" stroke="#6366F1" strokeWidth="2" strokeLinejoin="round"></path><path d="M2 17L12 22L22 17" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M2 7L12 12L22 7" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                                    </span> {tx.credits}
                                                </td>
                                                <td className="tx-action">
                                                    {tx.hasInvoice ? (
                                                        <span className="download-link" onClick={() => console.log('Download invoice for tx:', tx.id)}>
                                                            Download
                                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 9.293a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <span className="no-invoice">No Invoice</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="transactions-empty">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                <p>No transactions found</p>
                            </div>
                        )}
                    </div>

                    {/* <h5 className="fw-bold mb-3 mt-4">All Available Plans</h5> */}
                </div>
            )}

          

            {/* <Datatable ref={dataTable} config={config} viewType={'list'} props={props} history={props.history} />
            <style>
                {`
                    @media (max-width: 991px) {
                        .plans-list-container {
                            max-width: 100%;
                        }
                        .plans-list-container .w-100 {
                            max-width: 100%;
                        }
                        .plans-list-container .table-box {
                            min-width: 350px !important;
                        }
                        .plans-list-container .table-box th,
                        .plans-list-container .table-box td {
                            padding: 6px 4px !important;
                            font-size: 11px !important;
                        }
                        .plans-list-container .table-box th {
                            white-space: nowrap;
                        }
                    }
                `}
            </style> */}
        </div>
    )
}